import { useCallback, useRef, useState } from 'react';
import AIAssistantApi from '../../api/AIAssistantApi';
import type { ChatTypes } from 'devextreme-react/chat';
import { CustomStore, DataSource } from 'devextreme-react/common/data';
import {
    ALERT_TIMEOUT,
    assistant,
    user,
} from './data';

export const useApi = () => {

    const storeRef = useRef<ChatTypes.Message[] | null>(null);
    const customStoreRef = useRef<CustomStore | null>(null);
    const dataSourceRef = useRef<DataSource | null>(null);
    const currentSessionId = useRef<number | null>(null);

    const [alerts, setAlerts] = useState<ChatTypes.Alert[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    // Initialisation lazy — une seule fois
    if (storeRef.current === null) {
        storeRef.current = [];

        customStoreRef.current = new CustomStore({
            key: 'id',
            load: (): Promise<ChatTypes.Message[]> =>
                new Promise((resolve) => {
                    setTimeout(() => resolve([...storeRef.current!]), 0);
                }),
            insert: (message: ChatTypes.Message): Promise<ChatTypes.Message> =>
                new Promise((resolve) => {
                    setTimeout(() => {
                        storeRef.current!.push(message);
                        resolve(message);
                    });
                }),
        });

        dataSourceRef.current = new DataSource({
            store: customStoreRef.current,
            paginate: false,
        });
    }

    const load = useCallback((aiGroupId: number | null): void => {

        setIsLoading(true);

        storeRef.current!.length = 0;
        dataSourceRef.current!.reload();

        AIAssistantApi.GetAIAssistantSessionsAsync(aiGroupId).then((sessionIds) => {

            if (sessionIds.Result.length > 0) {

                const sessionId = sessionIds.Result.at(-1) ?? null;
                currentSessionId.current = sessionId;

                if (sessionId !== null) {
                    AIAssistantApi.GetAIAssistantMessagesAsync(sessionId).then((messages) => {

                        storeRef.current!.length = 0;

                        messages.Result.forEach((e) => {
                            storeRef.current!.push({
                                id: Date.now() + Math.random(), // Génère un id unique
                                text: e.Content,
                                author: e.Role === "user" ? user : assistant,
                                timestamp: new Date(),
                            });
                        });

                        dataSourceRef.current!.reload();
                        setIsLoading(false);
                    });
                } else {
                    setIsLoading(false);
                }
            } else {
                AIAssistantApi.CreateChatSessionAsync(aiGroupId).then((response) => {
                    currentSessionId.current = response.Result.Id;
                    setIsLoading(false);
                });
            }
        });
    }, []);

    const reset = useCallback((aiGroupId: number | null): void => {

        setIsLoading(true);

        storeRef.current!.length = 0;
        dataSourceRef.current!.reload();

        AIAssistantApi.CreateChatSessionAsync(aiGroupId).then((response) => {
            currentSessionId.current = response.Result.Id;
            setIsLoading(false);
        });
    }, []);

    const insertMessage = useCallback((data: ChatTypes.Message): void => {
        dataSourceRef.current!.store().push([{ type: 'insert', data }]);
    }, []);

    const alertLimitReached = useCallback((): void => {
        setAlerts([{
            message: 'Request limit reached, try again in a minute.',
        }]);

        setTimeout(() => {
            setAlerts([]);
        }, ALERT_TIMEOUT);
    }, []);

    const fetchAIResponse = useCallback(async (message: ChatTypes.Message): Promise<void> => {
        if (currentSessionId.current === null) {
            return;
        }

        try {
            const aiResponse = await AIAssistantApi.SendMessageAsync(currentSessionId.current, message.text);

            aiResponse.Result.Messages.forEach((e) => {
                insertMessage({
                    id: Date.now() + Math.random(),
                    timestamp: new Date(),
                    author: assistant,
                    text: e,
                });
            });
        } catch {
            alertLimitReached();
        }
    }, [alertLimitReached, insertMessage]);

    return {
        load,
        reset,
        alerts,
        isLoading,
        insertMessage,
        fetchAIResponse,
        dataSource: dataSourceRef.current!,
    };
};